import {StrictMode, Suspense, lazy} from "react";
import {createRoot} from "react-dom/client";
import * as Sentry from "@sentry/react";
import {Provider} from "react-redux";
import {
    createBrowserRouter,
    RouterProvider,
    RouteObject,
    Outlet,
} from "react-router-dom";
import {MantineProvider} from "@mantine/core";
import {ModalsProvider} from "@mantine/modals";
import {Notifications} from "@mantine/notifications";
import {store} from "./app/store";
import theme, {cssVariablesResolver} from "./app/theme";
import {useTranslation, I18nextProvider} from "react-i18next";
import i18n from "./i18n";
import {Intercom} from "./Intercom.tsx";
import {AuthorizationGate} from "./AuthorizationGate.tsx";
import "@fontsource/lato/400.css";
import "@fontsource/lato/700.css";
import "@fontsource/lato/900.css";
import "@fontsource/lato/400-italic.css";
import "@fontsource/lato/700-italic.css";
import "@fontsource/pt-serif/400.css";
import "@fontsource/pt-serif/700.css";
import "@mantine/notifications/styles.css";
import {ErrorBoundary} from "./ErrorBoundary.tsx";
import SessionExpired from "./SessionExpired.tsx";
import {ResponseTypeMigration} from "./ResponseTypeMigration.tsx";
import "./sentry";
import {ContentPage} from "./features/content/ContentPage.tsx";
import {TOPIC_PARTIAL_PATHS, LEGACY_PARTIAL_PATHS} from "./paths";
import {App as ChatLayout} from "./features/chat/App.tsx";
import {SettingsPage} from "./features/chat/SettingsPage.tsx";
import {RouterError} from "./RouterError.tsx";
import ChatView from "./features/chat/sliceChatComponents/ChatView.tsx";
import {DashboardLayout} from "./features/dashboard/DashboardLayout.tsx";
import {PortalsPage} from "./features/dashboard/PortalsPage.tsx";
import {MyWorkConsolidatedPage} from "./features/dashboard/MyWorkConsolidatedPage.tsx";
import {DashboardContentPage} from "./features/dashboard/DashboardContentPage.tsx";
import {ChatSearchHistoryPage} from "./features/dashboard/ChatSearchHistoryPage.tsx";
import {NewsPage} from "./features/dashboard/NewsPage.tsx";

// The control feature (both pages editor-gated) splits out of the entry
// bundle — production end users never download it. The root route's
// Suspense renders the fallback while the chunk loads.
const ControlPage = lazy(() =>
    import("./features/control/ControlPage.tsx").then((module) => ({
        default: module.ControlPage,
    }))
);
const ControlCasePage = lazy(() =>
    import("./features/control/case/ControlCasePage.tsx").then(
        (module) => ({default: module.ControlCasePage})
    )
);
// Standalone calculator tools, linked from elsewhere in the app; kept out of
// the entry bundle.
const CurrencyConverterPage = lazy(() =>
    import("./features/calculators/CurrencyConverterPage.tsx").then(
        (module) => ({default: module.CurrencyConverterPage})
    )
);
const VatCalculatorPage = lazy(() =>
    import("./features/calculators/VatCalculatorPage.tsx").then((module) => ({
        default: module.VatCalculatorPage,
    }))
);
const LegacyControlRedirect = lazy(() =>
    import("./features/control/LegacyControlRedirect.tsx").then((module) => ({
        default: module.LegacyControlRedirect,
    }))
);

// --- Define Routes ---

// React Router v6 doesn't support inline regex constraints, so we
// generate explicit routes for each known dokumentType (matching
// DibApp2020's constrained partialPath).
const PARTIAL_TYPES = [
    ...Object.values(TOPIC_PARTIAL_PATHS),
    ...LEGACY_PARTIAL_PATHS,
];

const topicBase = ":topicType/:topicName/:topicId";

// The control URL grammar, mirrored under /control and /control/case
// (react-router has no optional static segment). Same param shapes on both,
// so controlPath.ts can rebuild either variant from the other's params.
const controlRoutes = (
    base: string,
    element: RouteObject["element"]
): RouteObject[] => [
    {path: base, element},
    {path: `${base}/preview/:topicId`, element},
    {path: `${base}/:topicName/:topicId`, element},
    {
        path: `${base}/:topicName/:topicId/utfylling/:collectionName/:collectionId`,
        element,
    },
];

const partialRoutes: RouteObject[] = PARTIAL_TYPES.flatMap((dt) => [
    {
        path: `${topicBase}/${dt}/:dokumentName/:dokumentId/utfylling/:collectionName/:collectionId`,
        element: <ContentPage />,
    },
    {
        path: `${topicBase}/${dt}/:dokumentName/:dokumentId`,
        element: <ContentPage />,
    },
]);

// Routes that use the ChatLayout
const chatLayoutRoutes: RouteObject[] = [
    {
        element: <ChatLayout />,
        children: [
            {
                element: <DashboardLayout />,
                children: [
                    {
                        index: true,
                        element: <PortalsPage />,
                    },
                    {
                        path: "my-work",
                        element: <MyWorkConsolidatedPage />,
                    },
                    {
                        path: "content",
                        element: <DashboardContentPage />,
                    },
                    {
                        path: "chat-search",
                        element: <ChatSearchHistoryPage />,
                    },
                    {
                        path: "news",
                        element: <NewsPage />,
                    },
                ],
            },
            {
                path: "dashboard",
                element: <DashboardLayout basePath="/dashboard" />,
                children: [
                    {
                        index: true,
                        element: <PortalsPage />,
                    },
                    {
                        path: "my-work",
                        element: <MyWorkConsolidatedPage />,
                    },
                    {
                        path: "content",
                        element: <DashboardContentPage />,
                    },
                    {
                        path: "chat-search",
                        element: <ChatSearchHistoryPage />,
                    },
                    {
                        path: "news",
                        element: <NewsPage />,
                    },
                ],
            },
            {
                path: "chat/:threadId",
                element: <ChatView />,
            },
            {
                path: "settings",
                element: <SettingsPage />,
            },
            {
                path: "tools/currency-converter",
                element: <CurrencyConverterPage />,
            },
            {
                path: "tools/vat-calculator",
                element: <VatCalculatorPage />,
            },
            ...controlRoutes("control/case", <ControlCasePage />),
            ...controlRoutes("control", <ControlPage />),
            ...controlRoutes("compliance/case", <LegacyControlRedirect />),
            ...controlRoutes("compliance", <LegacyControlRedirect />),
            {
                path: "preview/:topicId",
                element: <ContentPage />,
            },
            ...partialRoutes,
            {
                path: `${topicBase}/utfylling/:collectionName/:collectionId`,
                element: <ContentPage />,
            },
            {
                path: `${topicBase}/segment/:segmentId`,
                element: <ContentPage />,
            },
            {
                path: topicBase,
                element: <ContentPage />,
            },
        ],
    },
];

const LoadingFallback = () => {
    const {t} = useTranslation();
    return <div>{t("common.loadingTranslations")}</div>;
};

const router = createBrowserRouter([
    {
        path: "/",
        element: (
            <Suspense fallback={null}>
                <Outlet />
            </Suspense>
        ),
        errorElement: <RouterError />,
        children: chatLayoutRoutes,
    },
]);

createRoot(document.getElementById("root")!, {
    onUncaughtError: Sentry.reactErrorHandler(),
    onCaughtError: Sentry.reactErrorHandler(),
    onRecoverableError: Sentry.reactErrorHandler(),
}).render(
    <StrictMode>
        <Provider store={store}>
            <MantineProvider
                theme={theme}
                cssVariablesResolver={cssVariablesResolver}
            >
                <Notifications />
                <ModalsProvider>
                    <I18nextProvider i18n={i18n}>
                        <ErrorBoundary fallback={<LoadingFallback />}>
                            <Suspense fallback={<LoadingFallback />}>
                                <SessionExpired />
                                <AuthorizationGate>
                                    <ResponseTypeMigration />
                                    <RouterProvider router={router} />
                                    <Intercom />
                                </AuthorizationGate>
                            </Suspense>
                        </ErrorBoundary>
                    </I18nextProvider>
                </ModalsProvider>
            </MantineProvider>
        </Provider>
    </StrictMode>
);
